import Card from '@components/Common/Card';
import CardBody from '@components/Common/Card/CardBody';

import Link from '@components/Common/Link';
import Button from '@components/Forms/Button';
import moment from 'moment';
import {toThousand} from '@lib/utils';
import {useTranslation} from 'next-i18next';

import styles from './style.module.css';
import {OrderTypeEnum} from '@enums/order';

const getStatusClass = (status: string) => {
  let statusClass;
  switch (status) {
    case 'waiting_for_payment':
      statusClass = styles.statusAwaitingPayment;
      break;
    default:
      statusClass = styles.statusSuccess;
  }
  return statusClass;
};

export const getStatusText = (status: string) => {
  let statusText;
  switch (status) {
    case 'waiting_for_payment':
      statusText = 'order_statuses.waiting_for_payment';
      break;
    case 'paid':
      statusText = 'order_statuses.payment_confirmed';
      break;
    case 'waiting_to_be_sent':
      statusText = 'order_statuses.processing_order';
      break;
    default:
      statusText = 'order_statuses.processing_order';
  }
  return statusText;
};

export default function OrderItemCard({
  order,
}: {
  order: {
    id: number;
    uuid: string;
    type: OrderTypeEnum;
    total_price_vat_included: number;
    status: string;
    delivery_method?: string;
    payment_method?: string;
    delivery_date?: Date;
    sent_date: Date | null;
    updated_at: Date;
  };
}) {
  const {t} = useTranslation('customer-orders');

  return (
    <Card classNames={'mb-3'}>
      <CardBody>
        <div className={styles.orderCardHeader}>
          <div className={styles.orderCardHeaderLeft}>
            <h4>{t<string>(order.type.toLowerCase())}</h4>

            <p>
              {moment(order.sent_date || order.updated_at).format('DD.MM.YYYY')}
            </p>
          </div>
          <div className={styles.orderCardHeaderRight}>
            <h4>{toThousand(order.total_price_vat_included)} €</h4>
            <p>{t<string>('vat.vat_included')}</p>
          </div>
        </div>
        <div
          className={`${styles.orderCardStatus} ${getStatusClass(
            order.status
          )}`}
        >
          <p>{t<string>(getStatusText(order.status))}</p>
        </div>
        <div className={styles.orderCardDetails}>
          <div className={styles.orderCardDetail}>
            <div className={styles.orderDetailLabel}>
              {t<string>('cards.order_id')}
            </div>
            <div className={styles.orderDetailValue}>#{order.id}</div>
          </div>
          <div className={styles.orderCardDetail}>
            <div className={styles.orderDetailLabel}>
              {t<string>('cards.delivery_date')}
            </div>
            <div className={styles.orderDetailValue}>
              {moment(order.delivery_date).format('DD.MM.YYYY')}
            </div>
          </div>
          <div className={styles.orderCardDetail}>
            <div className={styles.orderDetailLabel}>
              {t<string>('cards.delivery_method')}
            </div>
            <div className={styles.orderDetailValue}>
              {order.delivery_method}
            </div>
          </div>
          <div className={styles.orderCardDetail}>
            <div className={styles.orderDetailLabel}>
              {t<string>('cards.payment_method')}
            </div>
            <div className={styles.orderDetailValue}>
              {order.payment_method}
            </div>
          </div>
          <div className={styles.orderCardDetail}>
            <div className={styles.orderDetailLabel}>
              {t<string>('cards.total_price')}
            </div>
            <div className={styles.orderDetailValue}>
              {toThousand(order.total_price_vat_included)} €
            </div>
          </div>
        </div>
        <Link>
          <Button
            variant={'link'}
            href={`${t<string>('urls.order_details')}${order.uuid}`}
            block={true}
          >
            {t<string>('cards.view_order_details')}
          </Button>
        </Link>
      </CardBody>
    </Card>
  );
}
